const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const buildAdvisoryAction = (label = 'Open Advisory Content', tab = 'advisorycontent', roomId = 'advisory-partners') => ({
  label,
  tab,
  roomId,
});

export function buildAdvisoryPartnersRoomContext({
  room,
  advisoryOverview = null,
  primaryAction,
  secondaryAction,
}) {
  const stats = advisoryOverview?.stats || {};
  const partners = Array.isArray(advisoryOverview?.partners) ? advisoryOverview.partners : [];
  const inactiveCount = Number(stats.inactive_accounts || 0);
  const primaryTierCount = Number(stats.primary_tier_accounts || 0);
  const additionalRoleCount = Number(stats.additional_role_accounts || 0);
  const totalAccounts = Number(stats.total_accounts || 0);
  const firstInactivePartner = partners.find((partner) => String(partner?.status || '').toLowerCase() !== 'active');

  const priorityItems = [];

  if (firstInactivePartner) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Review first',
      title: `${firstInactivePartner.name || firstInactivePartner.email || 'An advisory partner'} no longer looks active`,
      detail: 'Advisory access should stay intentional. Confirm whether this person still belongs in the advisory circle.',
      location: 'In Advisory Partner Accounts',
    });
  }

  if (additionalRoleCount > 0) {
    priorityItems.push({
      tone: priorityItems.length ? 'next' : 'attention',
      eyebrow: priorityItems.length ? 'Then' : 'Do first',
      title: `${pluralize(additionalRoleCount, 'advisory account')} are layered on top of another primary tier`,
      detail: 'Decide whether layered access is still the right shape or whether someone should become an explicit advisory-tier account.',
      location: 'In Advisory Partner Accounts',
    });
  }

  if (!priorityItems.length) {
    priorityItems.push({
      tone: 'steady',
      eyebrow: 'Steady',
      title: 'The current advisory circle looks intentionally held',
      detail: 'Nothing urgent is surfacing right now, so stay close to who should have deeper pattern-seeing access and what they should be invited to respond to next.',
      location: 'In Advisory Partner Accounts',
    });
  }

  const statusTone = inactiveCount > 0
    ? 'attention'
    : additionalRoleCount > 0 || totalAccounts > 0
      ? 'next'
      : 'steady';

  const statusLabel = inactiveCount > 0
    ? 'Needs review'
    : additionalRoleCount > 0
      ? 'Clarify access'
      : totalAccounts > 0
        ? 'In motion'
        : 'Ready';

  return {
    id: room.id,
    variant: 'advisory-partners',
    statusTone,
    statusLabel,
    startTitle: inactiveCount > 0
      ? 'Start by confirming who should still carry advisory access'
      : additionalRoleCount > 0
        ? 'Make the advisory circle explicit before it becomes fuzzy'
        : 'Keep the advisory circle grounded in real mission trust',
    startDetail: 'This room should help you see who has access to the Advisory layer, whether that access is still intentional, and what kind of pattern-seeing partnership the mission actually needs right now.',
    signals: [
      {
        tone: totalAccounts > 0 ? 'next' : 'steady',
        text: totalAccounts > 0
          ? `${pluralize(totalAccounts, 'advisory account')} currently have access`
          : 'No advisory accounts are currently surfaced.',
      },
      {
        tone: additionalRoleCount > 0 ? 'next' : 'steady',
        text: additionalRoleCount > 0
          ? `${pluralize(additionalRoleCount, 'account')} are layered through additional roles`
          : 'No advisory accounts are currently layered through additional roles.',
      },
      {
        tone: inactiveCount > 0 ? 'attention' : 'steady',
        text: inactiveCount > 0
          ? `${pluralize(inactiveCount, 'advisory account')} may no longer be active`
          : 'No inactive advisory accounts are currently surfaced.',
      },
    ],
    metrics: [
      {
        label: 'Total accounts',
        value: totalAccounts,
        detail: 'People who currently carry advisory access',
        tone: totalAccounts > 0 ? 'next' : 'steady',
      },
      {
        label: 'Primary tier',
        value: primaryTierCount,
        detail: 'Accounts where advisory is the primary tier',
        tone: primaryTierCount > 0 ? 'next' : 'steady',
      },
      {
        label: 'Layered access',
        value: additionalRoleCount,
        detail: 'Accounts where advisory is granted as an additional role',
        tone: additionalRoleCount > 0 ? 'next' : 'steady',
      },
    ],
    priorityItems,
    watchListTitle: 'Advisory watchlist',
    watchListItems: [
      inactiveCount > 0
        ? {
            title: `${pluralize(inactiveCount, 'account')} need access review`,
            detail: 'Confirm whether these people should still be in the advisory circle.',
            tone: 'attention',
          }
        : null,
      additionalRoleCount > 0
        ? {
            title: `${pluralize(additionalRoleCount, 'account')} are still layered instead of primary`,
            detail: 'Layered access can be right, but it should stay intentional.',
            tone: 'next',
          }
        : null,
    ].filter(Boolean),
    primaryAction: primaryAction || buildAdvisoryAction(),
    secondaryAction: secondaryAction || null,
  };
}
